import { useLayoutEffect, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { officeNav } from '@nu/shared';
import NavIcon from '../components/NavIcon.jsx';
import Placeholder from './Placeholder.jsx';

// A department (Operations, Fleet...): a card for each screen in it that this
// person can open, like the NFUK platform. Daily tools and set-up screens
// (nav `setup`) are shown as two groups when a department has both. Every
// card is as tall as the tallest on the page, across the groups too.
export default function Department({ me }) {
  const { id } = useParams();
  const dept = officeNav(me.capabilities).sections.find((s) => s.id === id);
  const page = useRef(null);
  useLayoutEffect(() => {
    const el = page.current;
    if (!el) return undefined;
    const even = () => {
      el.style.removeProperty('--dept-card-h');
      const tallest = Math.max(0, ...[...el.querySelectorAll('.dept-card')].map((c) => c.offsetHeight));
      if (tallest) el.style.setProperty('--dept-card-h', `${tallest}px`);
    };
    even();
    const watch = new ResizeObserver(even);
    watch.observe(el);
    return () => watch.disconnect();
  }, [id, dept?.items.length]);
  if (!dept) return <Placeholder title="Page not found" />;
  // Daily tools first; set-up screens (Teams, Match-Ups) apart below them.
  const groups = [
    { id: 'daily', label: 'Daily tools', items: dept.items.filter((i) => !i.setup) },
    { id: 'setup', label: 'Set-up', items: dept.items.filter((i) => i.setup) },
  ].filter((g) => g.items.length);
  return (
    <div className="stack" ref={page}>
      <section className="card dept-head">
        <span className="dept-icon"><NavIcon name={dept.icon} size={30} /></span>
        <div>
          <h1>{dept.label}</h1>
          <p className="muted">{dept.description}</p>
        </div>
      </section>
      {groups.map((g) => (
        <section key={g.id} className="dept-group">
          {groups.length > 1 && <h2 className="dept-group-title">{g.label}</h2>}
          <div className="dept-cards">
            {g.items.map((i) => (
              <Link key={i.id} to={i.path} className="dept-card">
                <span className="dept-card-icon"><NavIcon name={i.icon} size={20} /></span>
                <strong>{i.label}</strong>
                <span className="muted small">{i.description}</span>
                <span className="dept-card-go">Open <ArrowRight size={16} aria-hidden="true" /></span>
              </Link>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
